// ═══════════════════════════════════════════════
// Offline Queue — File d'attente locale
// Stocke les actions en localStorage quand hors ligne
// Sync automatique quand le réseau revient
// ═══════════════════════════════════════════════

import { supabase } from './supabase.js';

const QUEUE_KEY = 'elixir_prep_offline_queue';
const PENDING_KEY = 'elixir_prep_pending_count';

// ── État réseau ──
let _online = navigator.onLine;
let _listeners = [];

export function isOnline() { return _online; }

export function onStatusChange(fn) {
  _listeners.push(fn);
  return () => { _listeners = _listeners.filter(f => f !== fn); };
}

function notifyListeners() {
  _listeners.forEach(fn => { try { fn(_online); } catch (e) {} });
}

window.addEventListener('online', () => {
  _online = true;
  notifyListeners();
  flushQueue(); // Sync automatique
});

window.addEventListener('offline', () => {
  _online = false;
  notifyListeners();
});

// ── File d'attente ──
function getQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY)) || []; }
  catch { return []; }
}

function saveQueue(q) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  localStorage.setItem(PENDING_KEY, String(q.length));
  notifyListeners();
}

export function getPendingCount() {
  return parseInt(localStorage.getItem(PENDING_KEY) || '0', 10);
}

// ── Ajouter une action à la file ──
export function enqueue(action) {
  // action = { type, table, data, id? }
  // type: 'insert' | 'update' | 'upsert' | 'delete'
  const q = getQueue();
  q.push({
    ...action,
    queued_at: new Date().toISOString(),
    id: action.id || (action.data?.id) || Date.now(),
  });
  saveQueue(q);
  console.log('[offline] Queued:', action.type, action.table, '| pending:', q.length);
}

// ── Exécuter une action ──
async function executeAction(action) {
  const { type, table, data, match } = action;

  switch (type) {
    case 'insert': {
      const { error } = await supabase.from(table).insert(data);
      if (error) throw error;
      break;
    }
    case 'update': {
      if (!match) throw new Error('update needs match');
      let query = supabase.from(table).update(data);
      Object.entries(match).forEach(([k, v]) => { query = query.eq(k, v); });
      const { error } = await query;
      if (error) throw error;
      break;
    }
    case 'upsert': {
      const { error } = await supabase.from(table).upsert(data, { onConflict: action.onConflict || 'id' });
      if (error) throw error;
      break;
    }
    case 'delete': {
      if (!match) throw new Error('delete needs match');
      let query = supabase.from(table).delete();
      Object.entries(match).forEach(([k, v]) => { query = query.eq(k, v); });
      const { error } = await query;
      if (error) throw error;
      break;
    }
    default:
      console.warn('[offline] Unknown action type:', type);
  }
}

// ── Vider la file (sync tout) ──
let _flushing = false;

export async function flushQueue() {
  if (_flushing) return;
  const q = getQueue();
  if (!q.length) return;
  if (!navigator.onLine) return;

  _flushing = true;
  console.log('[offline] Flushing queue:', q.length, 'actions');

  const failed = [];
  for (const action of q) {
    try {
      await executeAction(action);
    } catch (e) {
      console.error('[offline] Failed to execute:', action, e);
      failed.push(action);
    }
  }

  saveQueue(failed);
  _flushing = false;

  if (failed.length) {
    console.warn('[offline] ' + failed.length + ' actions still pending');
  } else {
    console.log('[offline] Queue empty — all synced');
  }
  notifyListeners();
}

// ── Helper : insert avec fallback offline ──
export async function safeInsert(table, data) {
  if (navigator.onLine) {
    try {
      const { error } = await supabase.from(table).insert(data);
      if (error) throw error;
      return { ok: true, offline: false };
    } catch (e) {
      // Fallback offline
      enqueue({ type: 'insert', table, data });
      return { ok: true, offline: true };
    }
  } else {
    enqueue({ type: 'insert', table, data });
    return { ok: true, offline: true };
  }
}

export async function safeUpdate(table, data, match) {
  if (navigator.onLine) {
    try {
      let query = supabase.from(table).update(data);
      Object.entries(match).forEach(([k, v]) => { query = query.eq(k, v); });
      const { error } = await query;
      if (error) throw error;
      return { ok: true, offline: false };
    } catch (e) {
      enqueue({ type: 'update', table, data, match });
      return { ok: true, offline: true };
    }
  } else {
    enqueue({ type: 'update', table, data, match });
    return { ok: true, offline: true };
  }
}

export async function safeUpsert(table, data, onConflict) {
  if (navigator.onLine) {
    try {
      const { error } = await supabase.from(table).upsert(data, { onConflict });
      if (error) throw error;
      return { ok: true, offline: false };
    } catch (e) {
      enqueue({ type: 'upsert', table, data, onConflict });
      return { ok: true, offline: true };
    }
  } else {
    enqueue({ type: 'upsert', table, data, onConflict });
    return { ok: true, offline: true };
  }
}

export async function safeDelete(table, match) {
  if (navigator.onLine) {
    try {
      let query = supabase.from(table).delete();
      Object.entries(match).forEach(([k, v]) => { query = query.eq(k, v); });
      const { error } = await query;
      if (error) throw error;
      return { ok: true, offline: false };
    } catch (e) {
      enqueue({ type: 'delete', table, match });
      return { ok: true, offline: true };
    }
  } else {
    enqueue({ type: 'delete', table, match });
    return { ok: true, offline: true };
  }
}

// Flush au démarrage si des actions en attente
setTimeout(() => { if (navigator.onLine) flushQueue(); }, 2000);

// Vider la file manuellement (actions bloquées)
export function clearQueue() {
  saveQueue([]);
  console.log('[offline] Queue cleared manually');
}

// Retirer les actions d'une session spécifique (évite les écrasements)
// Compte les actions en attente liées à une session (table sessions par id,
// ou toute table annexe par session_id) — utilisé avant la validation Prête.
export function getSessionPendingCount(sessionId) {
  const q = getQueue();
  return q.filter(a =>
    (a.table === 'elixir_prep_sessions' && (a.match?.id === sessionId || a.data?.id === sessionId))
    || a.data?.session_id === sessionId
    || a.match?.session_id === sessionId
  ).length;
}

export function clearSessionActions(sessionId) {
  const q = getQueue();
  const filtered = q.filter(a => !(a.table === 'elixir_prep_sessions' && (a.match?.id === sessionId || a.data?.id === sessionId)));
  if (filtered.length !== q.length) {
    saveQueue(filtered);
    console.log('[offline] Cleared', q.length - filtered.length, 'stale actions for session', sessionId);
  }
}
